import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Box } from "../../components/ui/box";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "../../components/ui/field";
import { Icon } from "../../components/ui/icon";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  SelectCombobox,
  SelectComboboxPopup,
  SelectComboboxTrigger,
  SelectEmpty,
  useSelectQuery,
} from "../../components/ui/select-combobox";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import { Text } from "../../components/ui/text";
import { Textarea } from "../../components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle-group";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useWidgetControls } from "./control-context";
import type {
  WidgetConfig,
  WidgetSettingField,
  WidgetSettingOption,
  WidgetSettingsGroup,
} from "./types";
import type { Omittable } from "../../lib/props";

/**
 * Renders a declarative widget settings schema (groups of fields) against a plain
 * value bag with a per-key `onChange`. The single field renderer behind the gear
 * Settings panel — one form for every config-composed widget.
 *
 * Base kinds (`text`/`textarea`/`number`/`toggle`/`segmented`/`select`) render
 * natively here; any other `kind` dispatches to a consumer-registered control, and
 * `dynamicOptions` resolves through a consumer-registered option source — the
 * domain-extensibility seam (see {@link WidgetControlsProvider}). This file stays
 * domain-free.
 */
export function SettingsFields({
  groups,
  values,
  onChange,
  disabled,
  lockedKeys,
  lockedTooltip,
}: Omittable<{
  groups: WidgetSettingsGroup[];
  values: WidgetConfig;
  onChange: (key: string, value: unknown) => void;
  disabled?: boolean;
  /** Field keys rendered disabled while the rest stay live — the preset
   *  locks of a curated instance (see `WidgetCatalogEntry.locks`). */
  lockedKeys?: ReadonlySet<string>;
  /** Why a locked field is disabled, shown on hover over the field. A
   *  disabled control cannot say so itself, and the panel carries no
   *  footnote: the reason rides the field it applies to. */
  lockedTooltip?: string;
}>) {
  const { controls, optionSources } = useWidgetControls();

  // No option-source hooks are called here. Each `dynamicOptions` field resolves
  // its source hook inside its own {@link DynamicOptions} child, so every hook
  // runs unconditionally in fixed order — no rules-of-hooks violation, and the
  // hook count never depends on registry size.
  return (
    <Box gap="md">
      {/* Section groups use gap="none" so the heading sits flush above its
          first control; the items within each section keep their own gap. */}
      {groups.map((group, gi) => (
        <Fragment key={`group-${gi}`}>
          {gi > 0 ? <Separator /> : null}
          <Box gap="none">
            {group.title ? <SectionLabel>{group.title}</SectionLabel> : null}
            <Box gap="sm">
              {group.fields.map((field) => {
                const locked = lockedKeys?.has(field.key) === true;
                const control = (
                  <SettingControl
                    field={field}
                    value={values[field.key]}
                    onCommit={onChange}
                    disabled={disabled || locked}
                    optionSources={optionSources}
                    controls={controls}
                  />
                );
                return locked && lockedTooltip ? (
                  <LockedSetting key={field.key} reason={lockedTooltip}>
                    {control}
                  </LockedSetting>
                ) : (
                  <Fragment key={field.key}>{control}</Fragment>
                );
              })}
            </Box>
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}

/** The hover surface over a locked field. Its controls are disabled, and a
 *  disabled control receives no pointer events, so the hover that opens the
 *  tooltip lands on this wrapper. Every descendant is made pointer-inert for
 *  the same reason — a disabled text field would otherwise swallow the hover
 *  instead of passing it up. The not-allowed cursor says "fixed" before the
 *  bubble does. */
function LockedSetting({ reason, children }: { reason: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Box
            className="**:pointer-events-none"
            cursor="not-allowed"
            data-slot="locked-setting"
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipPopup side="top">{reason}</TooltipPopup>
    </Tooltip>
  );
}

/** Small section heading. Sits in a 28px-tall row with the 12px label
 *  vertically centered; the muted tone is dimmed further via opacity so the
 *  headings recede behind the controls they group. */
function SectionLabel({ children }: { children: string }) {
  return (
    <Box height={28} justify="center" className="opacity-65">
      <Text size="xs" weight="medium" variant="muted">
        {children}
      </Text>
    </Box>
  );
}

/** Icon + label (+ optional helper copy below) — the left side of a settings row. */
function RowLabel({
  field,
  fill,
}: Omittable<{
  field: WidgetSettingField;
  /** Let the label take the row's spare width (toggle rows); omit when the
   * control side owns the spare width (select rows). */
  fill?: boolean;
}>) {
  return (
    <Box
      direction="row"
      align="center"
      gap="sm"
      grow={fill || undefined}
      shrink={fill ? undefined : false}
    >
      {field.icon ? <Icon name={field.icon} size="sm" color="muted" /> : null}
      <Box gap="none" grow={fill || undefined}>
        <Text size="sm">{field.label}</Text>
        {field.description ? (
          <Text size="xs" variant="muted">
            {field.description}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * Resolves a field's options and renders its control. Isolating the option-source
 * hook in its own component means it's called unconditionally, once per render,
 * obeying the rules of hooks regardless of other fields. `dynamicOptions` is
 * static schema, so this component's mount state for a field never changes.
 */
function DynamicOptions({
  source,
  children,
}: {
  source: () => WidgetSettingOption[];
  children: (options: WidgetSettingOption[]) => ReactNode;
}) {
  return <>{children(source())}</>;
}

/** Label (+ optional helper copy) over a control body — the shared field frame. */
export function ControlShell({
  label,
  description,
  children,
}: Omittable<{
  label: string;
  description?: string;
  children: ReactNode;
}>) {
  return (
    <Box gap="2xs">
      <Text size="xs" variant="muted">
        {label}
      </Text>
      {description ? (
        <Text size="xs" variant="muted">
          {description}
        </Text>
      ) : null}
      {children}
    </Box>
  );
}

/** The width every row-shaped control shares, so selects and the
 *  continuation inputs under them line up as one column. */
const CONTROL_COLUMN_WIDTH = 188;

/** Row layout: label left, a fixed-width compact control right — the shape of a
 *  select settings line. Exported so consumer-registered controls (the
 *  `WidgetControls` seam) render the same row as the built-in kinds. */
export function ControlRow({
  field,
  children,
}: {
  field: WidgetSettingField;
  children: ReactNode;
}) {
  return (
    <Box direction="row" align="center" justify="between" gap="sm">
      <RowLabel field={field} />
      <Box width={CONTROL_COLUMN_WIDTH} shrink={false}>
        {children}
      </Box>
    </Box>
  );
}

/** The control column alone: a `continuation` field's seat, flush under the
 *  row above's control and the same width, the label column left empty. */
function ContinuationRow({ children }: { children: ReactNode }) {
  return (
    <Box direction="row" justify="end">
      <Box width={CONTROL_COLUMN_WIDTH} shrink={false}>
        {children}
      </Box>
    </Box>
  );
}

/** The popup's rows: ungrouped options first, then one labeled group per
 *  `group`. Inside a searchable select the live query filters the rows and
 *  collapses the groups to one flat list — a group header over a single
 *  survivor says nothing the row does not. Inside a plain select the query
 *  is always blank, so every row mounts under its heading. */
function GroupedSelectItems({ options }: { options: WidgetSettingOption[] }) {
  const { hasQuery, matches } = useSelectQuery();
  const visible = options.filter((opt) => matches(opt.label));
  const flat = hasQuery ? visible : visible.filter((opt) => !opt.group);
  const groups: { name: string; options: WidgetSettingOption[] }[] = [];
  for (const opt of hasQuery ? [] : visible) {
    if (!opt.group) continue;
    const existing = groups.find((g) => g.name === opt.group);
    if (existing) existing.options.push(opt);
    else groups.push({ name: opt.group, options: [opt] });
  }
  const item = (opt: WidgetSettingOption) => (
    <SelectItem
      key={opt.value}
      value={opt.value}
      label={opt.label}
      description={opt.description}
      disabled={opt.disabled}
    >
      {opt.label}
    </SelectItem>
  );
  return (
    <>
      {flat.map(item)}
      {groups.map((group) => (
        <SelectGroup key={group.name}>
          <SelectGroupLabel>{group.name}</SelectGroupLabel>
          {group.options.map(item)}
        </SelectGroup>
      ))}
    </>
  );
}

function SettingControl({
  field,
  value,
  onCommit,
  disabled,
  optionSources,
  controls,
}: Omittable<{
  field: WidgetSettingField;
  value: unknown;
  onCommit: (key: string, value: unknown) => void;
  disabled?: boolean;
  optionSources: ReturnType<typeof useWidgetControls>["optionSources"];
  controls: ReturnType<typeof useWidgetControls>["controls"];
}>) {
  // The field's option source, or undefined when it carries a static list.
  // `dynamicOptions` is static schema, so this is always set or never set per
  // field — the {@link DynamicOptions} wrapper mounts consistently.
  const source = field.dynamicOptions ? optionSources[field.dynamicOptions] : undefined;
  // Static options lead, the source's follow: a select can carry a fixed
  // vocabulary and extend it with a live one (a workspace's fields).
  const withOptions = (render: (options: WidgetSettingOption[]) => ReactNode): ReactNode =>
    source ? (
      <DynamicOptions source={source}>
        {(dynamic) => render([...(field.options ?? []), ...dynamic])}
      </DynamicOptions>
    ) : (
      render(field.options ?? [])
    );

  switch (field.kind) {
    case "text":
    case "textarea":
      return (
        <TextControl
          field={field}
          value={typeof value === "string" ? value : ""}
          onCommit={onCommit}
          disabled={disabled}
        />
      );
    case "number":
      return (
        <NumberControl
          field={field}
          value={typeof value === "number" ? value : undefined}
          onCommit={onCommit}
          disabled={disabled}
        />
      );
    case "toggle":
      // Row layout: label left, switch right — a settings line, not a stacked field.
      return (
        <Box direction="row" align="center" justify="between" gap="sm">
          <RowLabel field={field} fill />
          <Switch
            // The row's label is a sibling, not a `<label>` — the switch has
            // no text of its own and would announce unnamed without this.
            aria-label={field.label}
            checked={value === true}
            disabled={disabled}
            onCheckedChange={(checked) => onCommit(field.key, checked)}
          />
        </Box>
      );
    case "segmented": {
      // Numeric configs (e.g. a column count) display through their string
      // option values; the control still commits strings — the consumer's
      // config write coerces back.
      const current =
        typeof value === "string"
          ? value
          : typeof value === "number"
            ? String(value)
            : undefined;
      return withOptions((options) => (
        <ControlShell label={field.label} description={field.description}>
          {/* Single mode (Base UI default): deselecting emits an empty group
              value, so for a non-clearable field the controlled `value` snaps
              the pressed item back. */}
          <ToggleGroup
            variant="outline"
            size="sm"
            value={current != null ? [current] : []}
            onValueChange={(groupValue) => {
              const v = groupValue[0];
              onCommit(field.key, v == null ? (field.clearable ? null : current) : v);
            }}
          >
            {options.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} disabled={disabled}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </ControlShell>
      ));
    }
    case "multiselect": {
      // The select row's grammar (icon, label, a trigger on the right) over a
      // multiple-selection popup: the value is a string array, the trigger
      // reads the one pick or "<first> +N", and the checks in the popup are
      // the state — so several picks are one visit.
      // Every trigger below carries `aria-label={field.label}`: the row's
      // label is a sibling RowLabel, not a `<label>`, so a trigger reading
      // only its placeholder would announce unnamed.
      return withOptions((options) => {
        const picked = Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
        const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
        const first = picked[0];
        const summary =
          first === undefined
            ? null
            : picked.length === 1
              ? labelOf(first)
              : `${labelOf(first)} +${picked.length - 1}`;
        if (options.length === 0) {
          return (
            <ControlRow field={field}>
              <Select items={options} value={[]} multiple disabled>
                <SelectTrigger aria-label={field.label} size="sm" disabled className="w-full min-w-0">
                  <SelectValue placeholder={field.emptyLabel ?? "Nothing to choose"} />
                </SelectTrigger>
              </Select>
            </ControlRow>
          );
        }
        return (
          <ControlRow field={field}>
            <Select
              items={options}
              multiple
              value={picked}
              onValueChange={(next) => onCommit(field.key, next)}
            >
              <SelectTrigger aria-label={field.label} size="sm" disabled={disabled} className="w-full min-w-0">
                <SelectValue placeholder={field.placeholder ?? "Select…"}>
                  {() => summary ?? <Text variant="muted">{field.placeholder ?? "Select…"}</Text>}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup>
                <GroupedSelectItems options={options} />
              </SelectPopup>
            </Select>
          </ControlRow>
        );
      });
    }
    case "select": {
      return withOptions((options) => {
        const current = options.find((o) => o.value === value);
        if (options.length === 0 && field.emptyLabel) {
          return (
            <ControlRow field={field}>
              <Select items={options} value={null} disabled>
                <SelectTrigger aria-label={field.label} size="sm" disabled className="w-full min-w-0">
                  <SelectValue placeholder={field.emptyLabel} />
                </SelectTrigger>
              </Select>
            </ControlRow>
          );
        }
        // A stored value no option carries reads as missing, not as unchosen,
        // when the field says how to word that.
        const stored = typeof value === "string" && value.length > 0;
        const placeholder =
          stored && current === undefined && field.missingLabel
            ? field.missingLabel
            : (field.placeholder ?? "Select…");
        const commit = (v: string | null) => {
          onCommit(field.key, v == null || v === "" ? (field.clearable ? null : v) : v);
        };
        const rows = (
          <>
            {field.clearable ? (
              <SelectItem value="" label="None">
                None
              </SelectItem>
            ) : null}
            <GroupedSelectItems options={options} />
          </>
        );
        if (field.searchable) {
          return (
            <ControlRow field={field}>
              <SelectCombobox
                items={options}
                value={current?.value ?? null}
                disabled={disabled ?? false}
                onValueChange={(v) => {
                  // The primitive reports a cleared field as null on close;
                  // only a picked row (None included) is a commit.
                  if (v === null) return;
                  commit(v);
                }}
              >
                <SelectComboboxTrigger size="sm" placeholder={placeholder} className="min-w-0" />
                <SelectComboboxPopup>
                  {rows}
                  <SelectEmpty>No matches</SelectEmpty>
                </SelectComboboxPopup>
              </SelectCombobox>
            </ControlRow>
          );
        }
        return (
          <ControlRow field={field}>
            <Select items={options} value={current?.value ?? null} onValueChange={commit}>
              <SelectTrigger aria-label={field.label} size="sm" disabled={disabled} className="w-full min-w-0">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectPopup>{rows}</SelectPopup>
            </Select>
          </ControlRow>
        );
      });
    }
    default: {
      // Open kind — dispatch to a consumer-registered control (the domain seam).
      const Control = controls[field.kind];
      if (!Control) return null;
      return <Control field={field} value={value} disabled={disabled} onCommit={onCommit} />;
    }
  }
}

/** Text / textarea — local state, commit on blur or Enter. */
function TextControl({
  field,
  value,
  onCommit,
  disabled,
}: Omittable<{
  field: WidgetSettingField;
  value: string;
  onCommit: (key: string, value: unknown) => void;
  disabled?: boolean;
}>) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const commit = () => {
    if (local !== value) onCommit(field.key, local);
  };
  if (field.continuation && field.kind !== "textarea") {
    return (
      <ContinuationRow>
        <Input
          size="sm"
          aria-label={field.label}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      </ContinuationRow>
    );
  }
  return (
    <Field disabled={disabled}>
      <FieldLabel>{field.label}</FieldLabel>
      {field.description ? <FieldDescription>{field.description}</FieldDescription> : null}
      {field.kind === "textarea" ? (
        <Textarea
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      ) : (
        <Input
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      )}
    </Field>
  );
}

/** Number — local string state, parse + commit on blur or Enter. */
function NumberControl({
  field,
  value,
  onCommit,
  disabled,
}: Omittable<{
  field: WidgetSettingField;
  value: number | undefined;
  onCommit: (key: string, value: unknown) => void;
  disabled?: boolean;
}>) {
  const [local, setLocal] = useState(value != null ? String(value) : "");
  useEffect(() => setLocal(value != null ? String(value) : ""), [value]);
  const commit = () => {
    const parsed = Number.parseFloat(local);
    if (Number.isNaN(parsed)) {
      setLocal(value != null ? String(value) : "");
      return;
    }
    let next = parsed;
    if (field.min != null) next = Math.max(field.min, next);
    if (field.max != null) next = Math.min(field.max, next);
    if (next !== value) onCommit(field.key, next);
    setLocal(String(next));
  };
  return (
    <Field disabled={disabled}>
      <FieldLabel>{field.label}</FieldLabel>
      {field.description ? <FieldDescription>{field.description}</FieldDescription> : null}
      <Input
        type="number"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
        disabled={disabled}
      />
    </Field>
  );
}
